import { amorphicStatic } from '@haventech/amorphic';
import { throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import safeJsonStringify from 'safe-json-stringify';
import { HavenStats } from '../havenStats/HavenStats';
import { get } from '../rxjs/http';
import { retry } from '../rxjs/operators/retry';
import { timeTaken } from '../rxjs/operators/timeTaken';
import { AgentLicense } from './../agent/AgentLicense';
import { serverOnly } from '../helpers';
import requestPromise from 'request-promise-native';
import { VPQFailureSeverity, VPQFailureType } from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import { CDMProducerResponse } from '../customer/Agent';
import axios from 'axios';
import { Utils } from '../Utils';
import { LoggerSingleton, Category, PiiLevelEnum } from '../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../logger/UILoggerSingleton';
import { PolicyAgent } from '../customer/PolicyAgent';

export type ProducerDesignation = {
  [designation: string]: boolean;
};

export type CdmProducerDesignations = {
  [producerId: string]: CdmProducerDesignationsResponse;
};

export type CdmProducerDesignationsResponse = {
  producerId: string;
  blueChipCircle: boolean;
  specialCare: boolean;
};

export type CdmDelegationResponse = {
  username: string;
  firstName: string;
  middleName: string;
  lastName: string;
  createdAt: Date;
  updatedAt: Date;
};

interface CdmAgentLicenseResponse {
  [state: string]: {
    license: string;
    startDate: string;
    endDate: string;
  };
}

export type AgencyCorpsRelationship = {
  agencyBpid: string;
  corpsBpid: string;
  agencyName: string;
  corpsName: string;
};

export interface VPQProducerInfo {
  producerId: string;
  role: string;
  agencyId: string;
  corpId?: string;
  businessUnit: string;
}

export interface VPQRequestBody {
  mode?: string;
  stage: string;
  applicationId: string;
  policy: {
    policyNumber?: string;
    code: string;
    term: number;
    signatureState: string;
    contractState: string;
    securedDate?: Date;
  };
  insured: {
    state: string;
  };
  owner: {
    state: string;
    signState?: string;
    signCounty?: string;
  };
  producers: VPQProducerInfo[];
}

export interface VPQFailure {
  type: VPQFailureType;
  severity: VPQFailureSeverity;
  requestType: string;
  message: string;
}

export interface VPQProducerResponse {
  producerId: string;
  role: 'Primary' | 'Secondary';
  qualified: boolean;
  failures: VPQFailure[];
}

export interface VPQResponse {
  producers: VPQProducerResponse[];
}

export type CdmUserResponse = {
  username: string;
  agency: CDMAgency;
};

export interface CDMAgency {
  businessPartnerId: string;
  name: string;
}

export type AgentRelationships = {
  [agentId: string]: {
    agencies: string[];
    corps?: {
      [corpId: string]: string[];
    };
  };
};

export type CDMProducerInfo = {
  producerId: string;
  statusCode: string;
  contractCode: string | null;
  firstName: string;
  lastName: string;
  middleName: string;
  nickName: string;
  agencyId: string;
  agencyCode?: string | null;
  agencyName: string;
  agencyType?: string | null;
  businessEmail: string;
  businessAddressLine1: string;
  businessAddressLine2: string;
  businessAddressLine3: string;
  businessCity: string;
  businessState: string;
  businessZip: string;
  corpId?: string;
  producerAltCategory: string;
  businessPhone: string;
  lastFourOfSSN?: string;
  lastFourOfTaxID?: string;
  businessUnit: string;
};

export interface TPDResponse {
  qualified: boolean;
  producerId: string;
  failure: VPQFailure;
}

export class CdmService {
  public getBpId(producerId: string) {
    if (producerId && producerId.toLowerCase().indexOf('tpd') !== 0) {
      // If not tpd user, remove the first two characters and put 4 leading 0s as that's
      // what cdm-producers expects
      return `0000${producerId.match(/\d+/)[0]}`;
    } else {
      return producerId;
    }
  }

  private processLicensesResponse(response: CdmAgentLicenseResponse, producerId: string): AgentLicense[] {
    const licenses: AgentLicense[] = Object.keys(response).map((state) => {
      const { license, startDate, endDate } = response[state];
      return new AgentLicense(state, license, producerId, new Date(startDate), new Date(endDate));
    });
    return licenses;
  }

  @serverOnly()
  private sendPerfStats(timeTaken: number) {
    HavenStats.singletonStatsDClient().timing(
      'AgentStateLicenseService.getStateLicensesFromCdm.request.time',
      timeTaken,
      { unit: 'milliseconds' }
    );
  }

  private logResponse(response, producerId: string, activityName: string): void {
    UILoggerSingleton.getInstance().info(
      {
        module: 'CDMService',
        function: activityName,
        category: UICategory.request,
        data: {
          producerId,
        },
      },
      safeJsonStringify(response)
    );
  }

  private logError(error: unknown, producerId: string, activityName: string): void {
    UILoggerSingleton.getInstance().error(
      {
        module: 'AgentStateLicenseService',
        function: activityName,
        category: UICategory.request,
        error: {
          data: {
            producerId,
          },
        },
      },
      error
    );
  }

  @serverOnly()
  private getProducerDesignations(producerId: string): Promise<CdmProducerDesignationsResponse> {
    const cdmUrl: string = amorphicStatic.config.nconf.get('cdmProducersUrl');
    const queryParams = `?basicResponse=true`;
    const uri: string = `${cdmUrl}/producers/${this.getBpId(producerId)}${queryParams}`;
    return get(uri)
      .pipe(
        tap((res: CdmProducerDesignationsResponse) =>
          this.logResponse(res, producerId, 'getProducerDesignationsFromCdm')
        ),
        map((res: CdmProducerDesignationsResponse) => {
          return {
            producerId,
            specialCare: res.specialCare,
            blueChipCircle: res.blueChipCircle,
          };
        }),
        retry(),
        catchError((error) => {
          this.logError(error, producerId, 'getProducerDesignationsFromCdm');
          return throwError(error);
        })
      )
      .toPromise();
  }

  public async getProducerDesignationsBulk(...producerIds: string[]): Promise<CdmProducerDesignations> {
    producerIds = Array.from(new Set(producerIds));
    const designationArray = await Promise.all(
      producerIds.map((producerId) => {
        return this.getProducerDesignations(producerId);
      })
    );
    const result: CdmProducerDesignations = {};
    designationArray.forEach((producerInfo) => {
      if (!result[producerInfo.producerId]) {
        result[producerInfo.producerId] = producerInfo;
      }
    });
    return result;
  }

  @serverOnly()
  public getStateLicenses(producerId: string): Promise<AgentLicense[]> {
    const cdmUrl: string = amorphicStatic.config.nconf.get('cdmProducersUrl');
    const uri: string = `${cdmUrl}/producers/${this.getBpId(producerId)}/licenses`;
    return get(uri)
      .pipe(
        timeTaken((time) => this.sendPerfStats(time)),
        tap((res: CdmAgentLicenseResponse) => this.logResponse(res, producerId, 'getStateLicensesFromCdm')),
        map((res: CdmAgentLicenseResponse) => this.processLicensesResponse(res, producerId)),
        retry(),
        catchError((error) => {
          this.logError(error, producerId, 'getStateLicensesFromCdm');
          return throwError(error);
        })
      )
      .toPromise();
  }

  @serverOnly()
  getProducerRelationships(producerId: string): Promise<AgencyCorpsRelationship[]> {
    const cdmUrl: string = amorphicStatic.config.nconf.get('cdmProducersUrl');
    const uri: string = `${cdmUrl}/producers/${this.getBpId(producerId)}/relationships`;
    return get(uri)
      .pipe(
        tap((res) => this.logResponse(res, producerId, 'getProducerRelationships')),
        map((res) => this.processRelationshipsResponse(res)),
        retry(),
        catchError((error) => {
          this.logError(error, producerId, 'getProducerRelationshipsFromCdm');
          return throwError(error);
        })
      )
      .toPromise();
  }

  private processRelationshipsResponse(response): AgencyCorpsRelationship[] {
    const agenciesResponse = response.agencies;
    const corpsResponse = response.corps;
    let agencies: AgencyCorpsRelationship[] = [];
    agencies = agenciesResponse.map((agency) => {
      const relationship: AgencyCorpsRelationship = {
        agencyBpid: agency.bpId,
        agencyName: agency.name,
        corpsBpid: null,
        corpsName: null,
      };
      return relationship;
    });
    const corps: AgencyCorpsRelationship[] = [];
    corpsResponse.forEach((corp) => {
      if (corp.agencies.length > 0) {
        const agencyCorp = corp.agencies.map((agencyCorp) => {
          const relationship: AgencyCorpsRelationship = {
            agencyBpid: agencyCorp.bpId,
            agencyName: agencyCorp.name,
            corpsBpid: corp.bpId,
            corpsName: corp.name,
          };
          return relationship;
        });
        corps.push(...agencyCorp);
      } else {
        //error - corps should always be tied to at least one agency
        UILoggerSingleton.getInstance().error(
          {
            module: 'CdmService',
            function: 'processRelationshipsResponse',
            category: UICategory.request,
            error: {
              isHumanRelated: false,
            },
          },
          'Error parsing the response from cdm producer relationships. No agencies are tied to this corp'
        );
      }
    });
    const relationshipResult = [...agencies, ...corps];
    return relationshipResult;
  }

  static async checkCdmVpq(requestBody: VPQRequestBody, runMock: boolean, example?: string): Promise<VPQResponse> {
    const cdmUrl: string = amorphicStatic.config.nconf.get('cdmProducersUrl');
    const uri: string = `${cdmUrl}/producer_qualification_checks`;
    let headers;
    //configure mock vpq if in lower env and mock example provided
    if (runMock && example) {
      headers = { mock: runMock, example: example };
    } else {
      headers = { mock: runMock };
    }

    const options = {
      url: uri,
      body: requestBody,
      json: true,
      timeout: 120000,
      headers: headers,
    };
    Utils.attachBearerTokenForDevOnlyRoutes(options);
    try {
      return requestPromise.post(options);
    } catch (error) {
      throwError({
        type: 'VPQError',
        message: 'VPQ I/O error: Could not communicate with CDM',
        rootCause: error,
      });
    }
  }

  static createVPQRequestFromPolicy(policy, stage): VPQRequestBody {
    const primaryPolicyAgent = policy.primaryPolicyAgent;
    const primaryAgent = primaryPolicyAgent.agent;
    const securedDate = policy.applicationSignedDate || Utils.now();

    const request = {
      stage: stage,
      applicationId: Utils.generateUuid(),
      policy: {
        policyNumber: policy.policyNumber,
        code: policy.product,
        term: policy.term,
        signatureState: policy.ownerPersonResidentialAddress.state,
        contractState: policy.ownerPersonResidentialAddress.state,
        securedDate: Utils.dateToYmd(securedDate),
      },
      insured: {
        state: policy.insured.address.state,
      },
      owner: {
        state: policy.ownerPersonResidentialAddress.state,
      },
      producers: [],
    };

    //populate producers array
    //check if policy agent has agency/corp information, otherwise use agency/corp info from Agent
    const usePolicyAgentAgencyCorpPrimary = primaryPolicyAgent && primaryPolicyAgent.agencyId !== null;

    const agentInfo = {
      producerId: this.getBpId(policy.primaryPolicyAgent.primaryAgent.producerId),
      role: 'Primary',
      agencyId: usePolicyAgentAgencyCorpPrimary ? primaryPolicyAgent.agencyId : primaryAgent.agencyId,
      corpId: usePolicyAgentAgencyCorpPrimary ? primaryPolicyAgent.corpId : primaryAgent.corpId,
      businessUnit: primaryAgent.businessUnit,
    };
    request.producers.push(agentInfo);
    //add additional agents to request
    policy.additionalPolicyAgents.forEach((polAgent) => {
      const usePolicyAgentAgencyCorpAdditional = polAgent && polAgent.agencyId !== null;
      const agent = polAgent.agent;
      const agentInfo = {
        producerId: this.getBpId(agent.producerId),
        role: 'Secondary',
        agencyId: usePolicyAgentAgencyCorpAdditional ? polAgent.agencyId : agent.agencyId,
        corpId: usePolicyAgentAgencyCorpAdditional ? polAgent.corpId : agent.corpId,
        businessUnit: agent.businessUnit,
      };
      request.producers.push(agentInfo);
    });
    UILoggerSingleton.getInstance().info(
      {
        module: 'CdmService',
        function: 'createVPQRequestFromPolicy',
        category: UICategory.request,
        context: {
          piiLevel: UIPiiLevelEnum.s2,
        },
        data: request,
      },
      `CDM VPQ Request created from policy, request.applicationId: ${request.applicationId}`
    );
    return request;
  }

  static getBpId(producerId: string) {
    if (producerId && producerId.toLowerCase().indexOf('tpd') !== 0) {
      // If not tpd user, remove the first two characters and put 4 leading 0s as that's
      // what cdm-producers expects
      return `0000${producerId.match(/\d+/)[0]}`;
    } else {
      return producerId;
    }
  }

  static parseVpqResponseErrors(response: VPQResponse): Array<VPQFailure> {
    const failedResponses = response.producers.filter((producerResponse) => producerResponse.qualified === false);
    let failures: Array<VPQFailure> = [];
    failedResponses.forEach((failureResponse) => {
      failures = failures.concat(failureResponse.failures);
    });
    return failures;
  }

  @serverOnly()
  public async getProducerDelegatesBatch(...producerIds: string[]) {
    const cdmUrl: string = amorphicStatic.config.nconf.get('cdmProducersUrl');
    const batchProducerDelegatesToPath = 'users/delegates_to';
    const queryParams = `?usernames=${producerIds.join('&usernames=')}`;
    const uri: string = `${cdmUrl}/${batchProducerDelegatesToPath}${queryParams}`;

    return get(uri)
      .pipe(
        tap((res: CdmDelegationResponse[]) =>
          this.logResponse(res, producerIds.join(', '), 'getProducerDelegatesBatch')
        ),
        retry(),
        catchError((error) => {
          this.logError(error, producerIds.join(', '), 'getProducerDelegatesBatch');
          return throwError(error);
        })
      )
      .toPromise();
  }

  @serverOnly()
  public async getUserInfoFromCDM(username: string): Promise<CdmUserResponse> {
    const cdmUrl: string = amorphicStatic.config.nconf.get('cdmProducersUrl');
    const userInfo = `users/${username.toLowerCase()}`;
    const uri: string = `${cdmUrl}/${userInfo}`;

    return get(uri)
      .pipe(
        tap((res: CdmUserResponse) => this.logResponse(res, username, 'getUserInfoFromCDM')),
        retry(),
        catchError((error) => {
          this.logError(error, username, 'getUserInfoFromCDM');
          return throwError(error);
        })
      )
      .toPromise();
  }

  @serverOnly()
  public async getAgentsFromCdm(agents: string[]): Promise<CDMProducerResponse[]> {
    try {
      const demographicRequestUrl: string = `${this.cdmEndpoint}/producers/basic/batch`;
      const options = {
        params: {
          producerIds: (agents || []).join(),
        },
        headers: { accept: 'application/json' },
      };
      Utils.attachBearerTokenForDevOnlyRoutes(options);
      const response = await axios.get(demographicRequestUrl, options);

      if (response.status === 200) {
        return response.data.result;
      }
      throw new Error(`Failed to get agent info from CDM: ${JSON.stringify(response)}`);
    } catch (err) {
      throw new Error(`Failed to get agent info from CDM: ${err.message}`);
    }
  }

  @serverOnly()
  public async getAgentRelationshipsFromCdm(agents: string[]): Promise<AgentRelationships> {
    try {
      const relationShipsRequestUrl: string = `${this.cdmEndpoint}/producers/relationships/batch`;
      const options = {
        headers: { accept: 'application/json' },
      };
      Utils.attachBearerTokenForDevOnlyRoutes(options);
      const response = await axios.post(
        relationShipsRequestUrl,
        {
          producerIds: agents,
        },
        options
      );

      if (response.status === 200) {
        // {
        //   "producers": {
        //   "1102000001": {
        //     "bpId": "1102000001",
        //       "agencies": [
        //       {
        //         "bpId": "1102000002",
        //         "name": "AGENCY 1",
        //         "code": "001"
        //       },
        //       {
        //         "bpId": "1102000003",
        //         "name": "AGENCY 2",
        //         "code": "002"
        //       }
        //     ],
        //       "corps": [
        //       {
        //         "bpId": "1102000004",
        //         "name": "CORP 1",
        //         "agencies": [
        //           {
        //             "bpId": "1102000005",
        //             "name": "AGENCY 3",
        //             "code": "003"
        //           }
        //         ]
        //       },
        //       {
        //         "bpId": "1102000006",
        //         "name": "CORP 2",
        //         "agencies": [
        //           {
        //             "bpId": "1102000007",
        //             "name": "AGENCY 4",
        //             "code": "004"
        //           },
        //           {
        //             "bpId": "1102000007",
        //             "name": "AGENCY 5",
        //             "code": "005"
        //           }
        //         ]
        //       }
        //     ]
        //   },
        //   "1102000002": {
        //     "bpId": "1102000002",
        //       "agencies": [
        //       {
        //         "bpId": "1102000002",
        //         "name": "AGENCY 1",
        //         "code": "001"
        //       }
        //     ],
        //       "corps": [
        //       {
        //         "bpId": "1102000004",
        //         "name": "CORP 1",
        //         "agencies": [
        //           {
        //             "bpId": "1102000005",
        //             "name": "AGENCY 3",
        //             "code": "003"
        //           }
        //         ]
        //       }
        //     ]
        //   }
        // },
        //   "errors": {
        //   "1000379999": {
        //     "message": "Broker not found"
        //   }
        // },
        //   "resultsCount": 2
        // }

        const responseStructure: AgentRelationships = {};
        const result = response.data.producers;
        agents.forEach((agentId) => {
          const bpId = this.getBpId(agentId);
          const agent = result[bpId];
          if (agent) {
            const agencyObjs = agent.agencies;
            const agencies = (agencyObjs || []).map((agency) => agency.bpId);
            responseStructure[agentId] = {
              agencies: agencies,
            };

            (agent.corps || []).forEach((corp) => {
              const corpAgency = corp.bpId;
              const agenciesUnderCorp = (corp.agencies || []).map((agency) => agency.bpId);
              responseStructure[agentId]['corps'] = {};
              responseStructure[agentId]['corps'][corpAgency] = agenciesUnderCorp;
            });
          }
        });
        return responseStructure;
      }
      throw new Error(`Failed to get agent relationships from CDM: ${JSON.stringify(response)}`);
    } catch (err) {
      throw new Error(`Failed to get agent relationships from CDM: ${err.message}`);
    }
  }

  @serverOnly()
  public async getAgencyMergersFromCdm(agencies: string[]): Promise<string[][]> {
    try {
      const mergersRequestUrl: string = `${this.cdmEndpoint}/agencies/merges`;
      const options = {
        params: {
          agencyIds: agencies.join(),
        },
        headers: { accept: 'application/json' },
      };
      Utils.attachBearerTokenForDevOnlyRoutes(options);
      const response = await axios(mergersRequestUrl, options);

      if (response.status === 200) {
        //[
        //   {
        //     "bpId": "0000510012",
        //     "merges": [
        //       {
        //         "bpId": "0000610012"
        //       }
        //     ]
        //   },
        //   {
        //     "bpId": "0000510013",
        //     "merges": [
        //       {
        //         "bpId": "0000610013"
        //       },
        //       {
        //         "bpId": "0000710013"
        //       }
        //     ]
        //   }
        // ]

        const responseStructure: string[][] = [];
        const result = response.data;
        result.forEach((item) => {
          const agencies = [item.bpId];
          item.merges.forEach((agency) => {
            agencies.push(agency.bpId);
          });
          responseStructure.push(agencies);
        });
        return responseStructure;
      }
      throw new Error(`Failed to get agent mergers from CDM: ${JSON.stringify(response)}`);
    } catch (err) {
      throw new Error(`Failed to get agency mergers from CDM: ${err.message}`);
    }
  }

  private get cdmEndpoint(): string {
    return amorphicStatic.config.nconf.get('cdmProducersUrl');
  }

  public async getProducerProxiesBatch(...producerIds: string[]) {
    const cdmUrl: string = amorphicStatic.config.nconf.get('cdmProducersUrl');
    const batchProducerProxiesForPath = 'users/proxies_for';
    const queryParams = `?usernames=${producerIds.join('&usernames=')}`;
    const uri: string = `${cdmUrl}/${batchProducerProxiesForPath}${queryParams}`;

    return get(uri)
      .pipe(
        tap((res: CdmDelegationResponse[]) => this.logResponse(res, producerIds.join(', '), 'getProducerProxiesBatch')),
        retry(),
        catchError((error) => {
          this.logError(error, producerIds.join(', '), 'getProducerProxiesBatch');
          return throwError(error);
        })
      )
      .toPromise();
  }

  public async refreshPolicyAgentInfo(policy): Promise<void> {
    try {
      const primaryAgencyCorp: AgencyCorpsRelationship = await this.getProducerAgencyCorps(
        policy.primaryPolicyAgent.agent.producerId
      );
      await this.updatePolicyAgent(policy.primaryPolicyAgent, primaryAgencyCorp);

      for (const additionalPolicyAgent of policy.additionalPolicyAgents) {
        const additionalAgencyCorp: AgencyCorpsRelationship = await this.getProducerAgencyCorps(
          additionalPolicyAgent.agent.producerId
        );
        await this.updatePolicyAgent(additionalPolicyAgent, additionalAgencyCorp);
      }
    } catch (e) {
      //Non-fatal error and does not stop execution.
      LoggerSingleton.getInstance().error(
        {
          module: 'VerifyAgentLicense',
          function: 'refreshPolicyAgentInfo',
          category: Category.request,
          error: {
            isHumanRelated: false,
            name: e.name,
            message: e.message,
            stack: e.stack,
          },
        },
        `Error calling CdmService for policy: ${policy.policyNumber}`
      );
    }
  }

  private async updatePolicyAgent(policyAgent: PolicyAgent, agencyCorpsRelationship: AgencyCorpsRelationship) {
    const policyAgentChanged: boolean =
      policyAgent.agencyId !== agencyCorpsRelationship.agencyBpid ||
      ((policyAgent.corpId || agencyCorpsRelationship.corpsBpid) &&
        policyAgent.corpId !== agencyCorpsRelationship.corpsBpid);

    policyAgent.agencyId = agencyCorpsRelationship.agencyBpid ? agencyCorpsRelationship.agencyBpid : null;
    policyAgent.corpId = agencyCorpsRelationship.corpsBpid ? agencyCorpsRelationship.corpsBpid : null;
    const agencyCorpOld: string = policyAgent.corpAgency;
    const agencyCorpNew: string = JSON.stringify(agencyCorpsRelationship);
    policyAgent.corpAgency = agencyCorpNew;

    //Updating policy agent even if there is no change. This way, we always know when the last time the agency info is refreshed b/c lastUpdatedTime will always get updated even if no changes.
    await policyAgent.persistorSave();

    let logInfoMsg: string;
    if (policyAgentChanged) {
      logInfoMsg = `Agency Corps info updated for policy ${policyAgent.policy.policyNumber}'s producer ${policyAgent.agent.producerId}: old info ${agencyCorpOld}, new info ${agencyCorpNew}.`;
    } else {
      logInfoMsg = `Agency Corps info refreshed for policy ${policyAgent.policy.policyNumber}'s producer ${policyAgent.agent.producerId}. No changes.`;
    }
    LoggerSingleton.getInstance().info(
      {
        module: 'CdmService',
        function: 'updatePolicyAgent',
      },
      logInfoMsg
    );
  }

  public async getProducerAgencyCorps(producerId: string): Promise<AgencyCorpsRelationship> {
    const cdmProducerInfo: CDMProducerInfo = await this.getProducerInfo(producerId);
    const agencyCorpResult: AgencyCorpsRelationship = {
      agencyBpid: cdmProducerInfo.agencyId,
      corpsBpid: cdmProducerInfo.corpId,
      agencyName: cdmProducerInfo.agencyName,
      corpsName: null,
    };
    return agencyCorpResult;
  }

  @serverOnly()
  public async getProducerInfo(producerId: string, securedDate?): Promise<CDMProducerInfo> {
    try {
      const producerBpid = this.getBpId(producerId);
      const agentSecuredDateUrl: string = `${this.cdmEndpoint}/producers/${producerBpid}`;

      const options = {
        params: {},
        headers: {
          accept: 'application/json',
        },
      };
      if (securedDate) {
        options.params = {
          asOfDate: securedDate ? securedDate : null,
        };
      }
      Utils.attachBearerTokenForDevOnlyRoutes(options);
      const response = await axios.get(agentSecuredDateUrl, options);
      if (response.status === 200) {
        return response.data;
      }
      throw new Error(`Failed to get agent info from CDM: ${JSON.stringify(response)}`);
    } catch (err) {
      if (err.response.status === 404) {
        throw new Error(`Agent is not active for policy secured date ${securedDate}`);
      } else {
        if (securedDate) {
          throw {
            message: `Unable to verify if Agent ${producerId} is valid for secured date ${securedDate}`,
            stack: err.stack,
          };
        } else {
          throw new Error(`Failed to get latest agent info from CDM: ${err.message}`);
        }
      }
    }
  }

  @serverOnly()
  public async checkOkToSolicit(producerId: string, state?): Promise<TPDResponse> {
    const agentSecuredDateUrl: string = `${this.cdmEndpoint}/ok-to-solicit/producer-id/${producerId}/state/${state}`;
    const options = {
      params: {},
      headers: {
        accept: 'application/json',
      },
      timeout: 60000,
    };
    Utils.attachBearerTokenForDevOnlyRoutes(options);
    const response = await axios.get(agentSecuredDateUrl, options);
    if (response.status === 200) {
      return response.data;
    }
    throw new Error(`Internal error occured: ${JSON.stringify(response)}`);
  }
}
